/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    PerformanceEvents as Events,
    PerformanceEvent as Event,
} from "@azure/msal-browser";

/**
 * Temporary solution to accommodate both 1p and 3p telemetry functionality.
 */

export const PerformanceEvents = {
    ...Events,
    PairwiseBrokerApplicationInitializeBrokering:
        "pairwiseBrokerApplicationInitializeBrokering",
    BrokerClientApplicationHandleBrokerHandshake:
        "brokerClientApplicationHandleBrokerHandshake",
    BrokerClientApplicationBrokeredSSOSilentRequest:
        "brokerClientApplicationBrokeredSSOSilentRequest",
    BrokerClientApplicationBrokeredSilentRequest:
        "brokerClientApplicationBrokeredSilentRequest",
    EmbeddedClientApplicationSendSSOSilentRequest: "embeddedSSOSilent",
    EmbeddedClientApplicationSendPopupRequest: "embeddedPopup",
    EmbeddedClientApplicationSendRedirectRequest: "embeddedRedirect",
    EmbeddedClientApplicationHandleRedirectRequest: "embeddedHandleRedirect",
    EmbeddedClientApplicationSendSilentRefreshRequest: "embeddedSilentRefresh",
} as const;
export type PerformanceEvents =
    (typeof PerformanceEvents)[keyof typeof PerformanceEvents];

export type PerformanceCallbackFunction = (events: PerformanceEvent[]) => void;

export const PerformanceEventAbbreviations: ReadonlyMap<string, string> =
    new Map([
        [
            PerformanceEvents.PairwiseBrokerApplicationInitializeBrokering,
            "PWBInitBrk",
        ],
        [
            PerformanceEvents.BrokerClientApplicationHandleBrokerHandshake,
            "BrkClientHandeBrkHandshake",
        ],
        [
            PerformanceEvents.BrokerClientApplicationBrokeredSSOSilentRequest,
            "BrkClientBrkSSOReq",
        ],
        [
            PerformanceEvents.BrokerClientApplicationBrokeredSilentRequest,
            "BrkClientBrkSltReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
            "EmbClientSendSSOReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
            "EmbClientSendPopupReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
            "EmbClientSendRedirectReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
            "EmbClientHandleRedirectReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
            "EmbClientSendSltRefreshReq",
        ],
    ]);

export type PerformanceEvent = Event & {
    brokerInteractionTimeoutMs?: number;
    brokerInteractionTimedOut?: boolean;
    brokerMessageTimeoutMs?: number;
    brokerMessageTimedOut?: boolean;
    brokerHandshakeTimeoutMs?: number;
    brokerHandshakeTimedOut?: boolean;
    brokerIframeTimeoutMs?: number;
    brokerIframeTimedOut?: boolean;
    synthetic?: boolean;
    // Profile telemetry identifier for MATS
    profileTelemetryId?: string;
    // Session identifier for MATS
    sessionId?: string;
    // Broker app indicator
    isBroker?: boolean;
    // For internal use to route broker events (ats, sso, popup, redirect) properly
    brokerEventName?: PerformanceEvents;
    // Embedded app id
    embeddedClientId?: string;
    // Embedded redirect uri
    embeddedRedirectUri?: string;
};

export const intFields: ReadonlySet<string> = new Set([
    "brokerInteractionTimeoutMs",
    "brokerMessageTimeoutMs",
    "brokerHandshakeTimeoutMs",
    "brokerIframeTimeoutMs",
]);
